import { useState } from 'react';
import { type RemuneracionItem } from '@lexicore/shared';
import { useAvisos } from '../../contextos/AvisosContext';
import { IconoCerrar } from '../../componentes/Iconos';
import { useLicitaciones } from '../../lib/consultas';
import { useAsignarBonoLicitacion } from '../../lib/mutaciones';

interface ModalAsignarBonoLicitacionProps {
  abierto: boolean;
  alCerrar: () => void;
  periodoMes: string;
  remuneraciones: RemuneracionItem[];
}

export function ModalAsignarBonoLicitacion({
  abierto,
  alCerrar,
  periodoMes,
  remuneraciones,
}: ModalAsignarBonoLicitacionProps) {
  const avisos = useAvisos();
  const licitacionesQuery = useLicitaciones();
  const bonoMut = useAsignarBonoLicitacion();

  const [remuneracionId, setRemuneracionId] = useState<string>(
    remuneraciones.length > 0 ? (remuneraciones[0]?.id ?? '') : '',
  );
  const [licitacionId, setLicitacionId] = useState<string>('');
  const [montoBonoNeto, setMontoBonoNeto] = useState<number>(350000);
  const [concepto, setConcepto] = useState('Premio por éxito en licitación adjudicada');

  if (!abierto) return null;

  const licitacionesAdjudicadas = (licitacionesQuery.data?.datos ?? []).filter(
    (lic: any) => lic.estado === 'ADJUDICADA' || lic.estado === 'CERRADA' || lic.estado === 'PRESENTADA',
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!licitacionId.trim()) {
      avisos.error('Por favor selecciona o ingresa la licitación asociada al bono.');
      return;
    }

    try {
      const remunSel = remuneraciones.find((r) => r.id === remuneracionId);

      await bonoMut.mutateAsync({
        remuneracionId: remuneracionId || undefined,
        licitacionId: licitacionId.trim(),
        nombreEmpleado: remunSel?.nombreEmpleado,
        cargo: remunSel?.cargo,
        montoBonoNeto: Number(montoBonoNeto),
        concepto: concepto.trim(),
        periodoMes,
      });

      avisos.exito(`Bono asignado exitosamente al colaborador.`);
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al asignar el bono por licitación.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div
        className="modal"
        style={{ maxWidth: 520, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🏆</span>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                Asignar Bono por Licitación Adjudicada
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                Período: <strong>{periodoMes}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={alCerrar}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: 4,
            }}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label htmlFor="bono_colab">Colaborador Beneficiario *</label>
            <select
              id="bono_colab"
              value={remuneracionId}
              onChange={(e) => setRemuneracionId(e.target.value)}
              required
            >
              {remuneraciones.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nombreEmpleado} — {r.cargo}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="bono_lic">Licitación de Mercado Público Vinculada *</label>
            {licitacionesAdjudicadas.length > 0 ? (
              <select
                id="bono_lic"
                value={licitacionId}
                onChange={(e) => setLicitacionId(e.target.value)}
                required
              >
                <option value="">-- Selecciona una Licitación --</option>
                {licitacionesAdjudicadas.map((l: any) => (
                  <option key={l.id} value={l.id}>
                    {l.codigoMercadoPublico ?? l.codigo ?? l.nombre} — {l.organismo?.nombre ?? l.organismoNombre ?? 'Organismo'}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="bono_lic"
                type="text"
                required
                placeholder="ID o Código de Licitación (ej. 2405-12-LR26)"
                value={licitacionId}
                onChange={(e) => setLicitacionId(e.target.value)}
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="bono_monto">Monto del Bono Neto (CLP) *</label>
            <input
              id="bono_monto"
              type="number"
              min="1000"
              step="1000"
              required
              value={montoBonoNeto}
              onChange={(e) => setMontoBonoNeto(Number(e.target.value))}
            />
          </div>

          <div className="form-group">
            <label htmlFor="bono_motivo">Motivo / Justificación para Auditoría *</label>
            <input
              id="bono_motivo"
              type="text"
              required
              placeholder="Ej: Premio por éxito en adjudicación Hospital San Juan de Dios"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              paddingTop: 12,
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={bonoMut.isPending}>
              {bonoMut.isPending ? 'Asignando…' : 'Confirmar y Asignar Bono'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
